/* Luxe & Gloss shared runtime. Feature behavior stays in lane-owned assets. */

window.theme = window.theme || { locales: {} }
window.Shopify = window.Shopify || {}

window.theme.debounce = function (callback, wait = 200) {
  let timeout
  return function (...args) {
    clearTimeout(timeout)
    timeout = setTimeout(() => callback.apply(this, args), wait)
  }
}

window.theme.throttle = function (callback, timeFrame = 200) {
  let lastTime = 0
  return function (...args) {
    const now = Date.now()
    if (now - lastTime >= timeFrame) {
      callback.apply(this, args)
      lastTime = now
    }
  }
}

window.theme.createCookie = function (name, value, days) {
  const expires = days
    ? `; expires=${new Date(Date.now() + (days * 86400000)).toUTCString()}`
    : ''

  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}${expires}; path=/; SameSite=Lax`
}

window.theme.calcTimeAgo = function (timestamp) {
  const locales = window.theme.locales?.times || {}
  const diff = Date.now() - Number(timestamp)
  let count
  let unit

  if (!Number.isFinite(diff)) return ''

  if (diff < 60000) {
    return `${locales.moments || 'moments'} ${locales.ago || 'ago'}`
  } else if (diff < 3.6e+6) {
    count = Math.round(diff / 60000)
    unit = count === 1 ? (locales.minute || 'minute') : (locales.minutes || 'minutes')
  } else if (diff < 8.64e+7) {
    count = Math.round(diff / 3.6e+6)
    unit = count === 1 ? (locales.hour || 'hour') : (locales.hours || 'hours')
  } else {
    count = Math.round(diff / 8.64e+7)
    unit = count === 1 ? (locales.day || 'day') : (locales.days || 'days')
  }

  return `${count} ${unit} ${locales.ago || 'ago'}`
}

window.Shopify.formatMoney = function (cents, moneyFormat = window.Shopify.moneyFormat || '${{amount}}') {
  const numericCents = typeof cents === 'string' ? cents.replace('.', '') : cents
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
  const match = String(moneyFormat).match(placeholderRegex)

  if (!match) return String(numericCents ?? '')

  const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
    const value = Number(number)
    if (!Number.isFinite(value)) return '0'

    const parts = (value / 100).toFixed(precision).split('.')
    const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`)
    return whole + (parts[1] ? `${decimal}${parts[1]}` : '')
  }

  const formats = {
    amount: () => formatWithDelimiters(numericCents, 2),
    amount_no_decimals: () => formatWithDelimiters(numericCents, 0),
    amount_with_comma_separator: () => formatWithDelimiters(numericCents, 2, '.', ','),
    amount_no_decimals_with_comma_separator: () => formatWithDelimiters(numericCents, 0, '.', ',')
  }
  const formatted = formats[match[1]] ? formats[match[1]]() : formatWithDelimiters(numericCents, 2)

  return String(moneyFormat).replace(placeholderRegex, formatted)
}

window.Shopify.resizeImage = function (src, size, crop = '') {
  if (typeof src !== 'string' || !src) return src

  return src
    .replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)+\./g, '.')
    .replace(/\.jpg|\.png|\.gif|\.jpeg/g, (match) => {
      const cropSuffix = crop ? `_crop_${crop}` : ''
      return `_${size}${cropSuffix}${match}`
    })
}

const viewportObserver = 'IntersectionObserver' in window
  ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => entry.target.classList.toggle('viewport-entered', entry.isIntersecting))
    }, { threshold: 0.4 })
  : null

function initializeSharedUi (root = document) {
  if (window.bootstrap?.Tooltip) {
    root.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((element) => {
      window.bootstrap.Tooltip.getOrCreateInstance(element)
    })
  }

  if (window.bootstrap?.Popover) {
    root.querySelectorAll('[data-bs-toggle="popover"]').forEach((element) => {
      window.bootstrap.Popover.getOrCreateInstance(element)
    })
  }

  root.querySelectorAll('.btn.shopify-challenge__button').forEach((element) => {
    element.classList.add('btn-primary')
  })

  root.querySelectorAll('.errors').forEach((element) => {
    element.classList.add('alert', 'alert-danger')
  })

  root.querySelectorAll('.shopify-section > .shopify-app-block').forEach((element) => {
    element.classList.add('container')
  })

  if (viewportObserver) {
    root.querySelectorAll('.viewport-detect:not([data-lg-observed])').forEach((element) => {
      element.dataset.lgObserved = 'true'
      viewportObserver.observe(element)
    })
  }

  root.querySelectorAll('.shopify-localization-form button:not([data-lg-localization-bound])').forEach((button) => {
    button.dataset.lgLocalizationBound = 'true'
    button.addEventListener('click', () => {
      const form = button.closest('form')
      const countryInput = form?.querySelector('[name="country_code"]')
      if (!form || !countryInput || !button.dataset.isoCode) return

      countryInput.value = button.dataset.isoCode
      form.requestSubmit()
    })
  })
}

let scrollFrame
window.addEventListener('scroll', () => {
  if (scrollFrame) return
  scrollFrame = window.requestAnimationFrame(() => {
    document.documentElement.classList.toggle('has-scrolled', window.scrollY > 0)
    scrollFrame = null
  })
}, { passive: true })

let swiperScriptPromise

function loadSwiper (source) {
  if (window.Swiper) return Promise.resolve(window.Swiper)
  if (!source) return Promise.reject(new Error('Swiper source is unavailable.'))
  if (swiperScriptPromise) return swiperScriptPromise

  const absoluteSource = new URL(source, document.baseURI).href
  const script = document.createElement('script')

  swiperScriptPromise = new Promise((resolve, reject) => {
    const cleanup = () => {
      script.removeEventListener('load', handleLoad)
      script.removeEventListener('error', handleError)
    }
    const handleLoad = () => {
      cleanup()
      if (window.Swiper) resolve(window.Swiper)
      else reject(new Error('Swiper did not initialize.'))
    }
    const handleError = () => {
      cleanup()
      reject(new Error('Swiper failed to load.'))
    }

    script.addEventListener('load', handleLoad)
    script.addEventListener('error', handleError)
    script.src = absoluteSource
    script.defer = true
    script.dataset.lgSwiperLoader = 'true'
    document.head.append(script)
  }).catch(error => {
    script.remove()
    swiperScriptPromise = null
    throw error
  })

  return swiperScriptPromise
}

class SwiperSlider extends HTMLElement {
  connectedCallback () {
    this.initialize()
  }

  disconnectedCallback () {
    this.initializationToken = null
    this.slider?.destroy?.(true, true)
    this.slider = null
  }

  async initialize () {
    const sliderElement = this.querySelector('.swiper')
    if (!sliderElement) return

    const initializationToken = Symbol('swiper-initialization')
    this.initializationToken = initializationToken

    if (!window.Swiper) {
      try {
        await loadSwiper(this.dataset.swiperScriptSrc)
      } catch {
        return
      }
    }

    if (!this.isConnected || this.initializationToken !== initializationToken) return

    this.slider?.destroy?.(true, true)
    this.slider = new window.Swiper(sliderElement, {
      speed: Number(this.dataset.sliderSpeed),
      autoplay: this.dataset.sliderAutoplay === '0'
        ? undefined
        : { delay: Number(this.dataset.sliderAutoplay) * 1000 },
      navigation: {
        enabled: this.dataset.sliderNavigation === 'true',
        prevEl: this.querySelector('.swiper-button-prev'),
        nextEl: this.querySelector('.swiper-button-next')
      },
      pagination: {
        enabled: this.dataset.sliderPagination === 'true',
        el: this.querySelector('.swiper-pagination'),
        type: 'bullets',
        dynamicBullets: true,
        dynamicMainBullets: 2
      },
      scrollbar: {
        enabled: this.dataset.sliderScrollbar === 'true',
        el: this.querySelector('.swiper-scrollbar'),
        draggable: true
      },
      breakpoints: {
        0: { slidesPerView: Number(this.dataset.breakpointMobile) },
        600: { slidesPerView: Number(this.dataset.breakpointTablet) },
        1200: { slidesPerView: Number(this.dataset.breakpointDesktop) }
      },
      rewind: true
    })
  }
}

if (!customElements.get('swiper-slider')) {
  customElements.define('swiper-slider', SwiperSlider)
}

initializeSharedUi()
document.addEventListener('shopify:section:load', (event) => initializeSharedUi(event.target))
